
import { AxiosResponse } from 'axios'
import { endOfDay, endOfWeek, format, startOfDay, startOfWeek } from 'date-fns'
import Litepicker from 'litepicker'
import { defineComponent } from 'vue'
import AppointmentTypeService from '../common/appointmentTypeService'
import ReportingService from '../common/reportingService'
import SponsorService from '../common/sponsorService'
import UserService from '../common/userService'
import Appointment from '../models/appointment'
import { AppointmentType } from '../models/appointmentType'
import { ReportAppointment, ReportCount, ReportResult } from '../models/reportResult'
import { Sponsor } from '../models/sponsor'
import User from '../models/user'

type DateRangeType = 'range' | 'past' | 'future'

export default defineComponent({
  name: 'Reporting',

  data () {
    return {
      dateRangeType: 'range' as DateRangeType,
      /** report specification form */
      reportSpec: {
        start: startOfWeek(new Date()) as Date | null,
        end: endOfWeek(new Date()) as Date | null,
        sponsorIds: [] as Array<number>,
        appointmentTypeIds: [] as Array<number>,
        reviewerIds: [] as Array<number>,
        includeCancelled: true,
        includeRescheduled: true
      },
      results: null as unknown as ReportResult,
      countByReviewers: [],
      countByAppointmentType: [],
      countBySponsor: [],
      reportUrl: '',
      reviewers: null as unknown as Array<User>,
      sponsors: [] as Array<Sponsor>,
      appointmentTypes: null as unknown as Array<AppointmentType>,
      startPicker: null as unknown as Litepicker,
      endPicker: null as unknown as Litepicker,
      sponsorSearchKey: '',
      sponsorSearchPos: { bottom: 0, left: 0 },
      showSponsorSelection: false,
      resultsView: 'appointments'
    }
  },

  computed: {
    /** list of filtered sponsors */
    filteredSponsors (): Array<Sponsor> {
      if (this.sponsorSearchKey !== '') {
        return this.sponsors.filter(s => RegExp(this.sponsorSearchKey, 'i').test(s.name))
      } else {
        return this.sponsors
      }
    },

    /** list of selected sponsors */
    selectedSponsors (): Array<Sponsor> {
      return this.sponsors.filter(s => this.reportSpec.sponsorIds.findIndex(id => s.id === id) !== -1)
    },

    /** list of report counts */
    selectedCounts (): Array<ReportCount> {
      if (this.resultsView === 'reviewer') {
        return this.results.reviewerCounts
      } else if (this.resultsView === 'sponsor') {
        return this.results.sponsorCounts
      } else {
        return this.results.appointmentTypeCounts
      }
    }
  },

  watch: {
    /**
     * Sets date range types; range, past, future
     */
    dateRangeType () {
      if (this.dateRangeType === 'range') {
        this.reportSpec.start = startOfWeek(new Date())
        this.reportSpec.end = endOfWeek(new Date())
      } else if (this.dateRangeType === 'past') {
        this.reportSpec.start = null
        this.reportSpec.end = endOfDay(new Date())
      } else if (this.dateRangeType === 'future') {
        this.reportSpec.start = startOfDay(new Date())
        this.reportSpec.end = null
      }

      if (this.reportSpec.start) {
        console.log('s')
        this.startPicker.setDate(this.reportSpec.start)
      }

      if (this.reportSpec.end) {
        console.log('e')
        this.endPicker.setDate(this.reportSpec.end)
      }
    },

    /**
     * Sponsor search
     */
    sponsorSearchKey () {
      const searchbar = document.querySelector('.sponsor-search')?.getBoundingClientRect()
      if (searchbar) {
        this.sponsorSearchPos.left = 10
        this.sponsorSearchPos.bottom = window.innerHeight - searchbar.top + 10
      }
    },

    reportSpec: {
      // genereate report
      handler () {
        this.generateReport()
      },
      deep: true,
      immediate: true
    }
  },

  mounted () {
    // load appointment types
    AppointmentTypeService.getAll().then(res => {
      this.appointmentTypes = res.data
      this.appointmentTypes.forEach(at => this.reportSpec.appointmentTypeIds.push(at.id))
    })

    // load sponsors
    SponsorService.getAll().then(res => {
      this.sponsors = res.data
    })

    // load OSP staffers that are reviewers
    UserService.getOspStaff().then(res => {
      this.reviewers = res.data.filter(u => u.permissions.findIndex(p => p.code === 'REVU') !== -1)
      this.reviewers.forEach(r => this.reportSpec.reviewerIds.push(r.id))
    })

    this.setupTimePickers()
  },

  methods: {
    /**
     * add sponsor to report spec
     */
    addSponsor (sponsorId: number) {
      this.reportSpec.sponsorIds.push(sponsorId)
      this.sponsorSearchKey = ''
    },

    /**
     * download datafrom report spec
     */
    download () {
      ReportingService.downloadReport(this.reportSpec).then(res => {
        this.reportUrl = res.data
        window.location = this.reportUrl as any
      })
    },

    /**
     * Formats the given date in the specified format
     */
    formatTime (date: Date, formatString: string): string {
      return format(date, formatString)
    },

    /**
     * remove sponsor from report spec
     */
    removeSponsor (sponsorId: number) {
      const ind = this.reportSpec.sponsorIds.findIndex(x => x === sponsorId)
      this.reportSpec.sponsorIds.splice(ind, 1)
    },

    /**
     * sets start and end time from picker calendar
     */
    setupTimePickers () {
      this.startPicker = new Litepicker({ element: document.querySelector('.start-time'), singleMode: true, onSelect: (date) => { console.log('setting start'); this.reportSpec.start = date } })
      this.startPicker.setDate(this.reportSpec.start)

      this.endPicker = new Litepicker({ element: document.querySelector('.end-time'), singleMode: true, onSelect: (date) => { console.log('setting end'); this.reportSpec.end = date } })
      this.endPicker.setDate(this.reportSpec.end)
    },

    /**
     * generate report from spec
     */
    generateReport () {
      ReportingService.generateReport(this.reportSpec).then((res: AxiosResponse<ReportResult>) => {
        res.data.appointments.forEach((r: ReportAppointment) => {
          r.Start = new Date(r.Start)
          r.End = new Date(r.End)
          r.ScheduledOn = new Date(r.ScheduledOn)
          r.ReviewDeadline = new Date(r.ReviewDeadline)
        })
        this.results = res.data
      })
    }
  }
})
